import { useState } from "react";
import { Link } from "react-router-dom";
import { gossipFaqs } from "@/mocks/gossipFaqs";
import Seo from "@/components/base/Seo";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_URL } from "@/utils/site";

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "常见问题", item: `${SITE_URL}/faq` },
    ],
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: "zh-CN",
    mainEntity: gossipFaqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return (
    <>
      <Seo
        title="常见问题 | 瓜料动态"
        description="瓜料动态常见问题汇总：更新频率、信源判断标准、话题为何停止更新、订阅内容范围等，一次说清楚。"
        keywords="瓜料动态常见问题,瓜料动态更新频率,瓜料动态信源,瓜料动态订阅"
        path="/faq"
        jsonLd={[breadcrumbLd, faqLd]}
      />
      <Layout>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
            <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">常见问题</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              关于更新节奏、信源判断和内容边界，我们把最常被问到的几个问题整理在这里。如果还有疑问，欢迎写信告诉我们。
            </p>
          </div>
        </div>

        <div className="mx-auto grid max-w-shell gap-10 px-4 py-12 md:px-6 lg:grid-cols-[1.7fr_1fr]">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {gossipFaqs.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div key={item.question} className="border-b border-background-200 last:border-0">
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="flex items-center gap-3">
                      <span className="font-label text-sm font-bold text-primary-500">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="font-heading text-base font-bold text-foreground-950 md:text-lg">
                        {item.question}
                      </span>
                    </span>
                    <i
                      className={`ri-add-line shrink-0 text-lg text-primary-600 transition-transform ${
                        isOpen ? "rotate-45" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <p className="px-5 pb-6 pl-14 text-sm leading-[1.9] text-foreground-600">{item.answer}</p>
                  )}
                </div>
              );
            })}
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-lightbulb-line text-secondary-600" />
                还有别的疑问？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                发现线索、内容纠错或合作沟通，都可以直接写信联系我们，通常会在两个工作日内回复。
              </p>
              <a
                href="mailto:hello@gualiaodongtai.com"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-line" /> hello@gualiaodongtai.com
              </a>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">继续浏览</h2>
              <ul className="mt-3 space-y-1">
                <li>
                  <Link
                    to="/#live"
                    className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                  >
                    今日更新时间线 <i className="ri-arrow-right-s-line text-foreground-400" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                  >
                    关于瓜料动态 <i className="ri-arrow-right-s-line text-foreground-400" />
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </Layout>
    </>
  );
}